import classNames from 'classnames';
import React, {
  forwardRef,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Box, { BoxProps } from 'ui-box';
import { defaultColors, majorScale, minorScale } from '.';
import { Button } from './Button';
import { Heading } from './Heading';
import { Icon } from './Icon';
import { InputGroup } from './InputGroup';
import { Menu, MenuDivider } from './Menu';
import { IPopoverProps, Popover } from './Popover';
import { Spinner } from './Spinner';

interface Identifiable {
  id: number | string;
}

export interface ISelectProps<T extends Identifiable> extends BoxProps<any> {
  placement?: IPopoverProps['placement'];
  placeholder?: string;
  value?: T['id'] | null;
  resolveSelectedValue?: (identifier: T['id']) => Promise<T>;
  fill?: boolean;
  disabled?: boolean;
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  items?: T[];
  isLoading?: boolean;
  title?: string;
  hideSearchInput?: boolean;
  searchPlaceholder?: string;
  itemRenderer?: (item: T) => ReactNode;
  valueRenderer?: (item: T) => string;
  detailViewRenderer?: (item?: T) => ReactNode;
  noResultsMessage?: string;
  query?: string;
  onQueryChange?: (query: string) => void;
  onSelect?: (item: T) => void;
  filterItems?: (item: T, query: string) => boolean;
  closesOnSelect?: boolean;
}

export const Select = <T extends Identifiable>({
  placement,
  placeholder,
  children,
  options = [],
  value,
  disabled = false,
  hideSearchInput = false,
  fill = true,
  items = [],
  isLoading = false,
  isOpen: defaultIsOpen = false,
  onOpen,
  onClose,
  title,
  searchPlaceholder,
  itemRenderer = (item) => <p>{item.id}</p>,
  valueRenderer = (item) => item.id + '',
  detailViewRenderer,
  resolveSelectedValue,
  noResultsMessage,
  query,
  onQueryChange,
  onSelect,
  filterItems,
  closesOnSelect = true,
  className,
  ...props
}: ISelectProps<T>) => {
  const intl = useIntl();

  searchPlaceholder =
    searchPlaceholder ||
    intl.formatMessage({
      defaultMessage: 'Search...',
    });
  noResultsMessage =
    noResultsMessage ||
    intl.formatMessage({
      defaultMessage: 'No results found',
    });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedItem, setSelectedItem] = useState<T>();
  const [activeRef, setActiveRef] = useState<HTMLDivElement | null>(null);
  const scrollContainer = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(defaultIsOpen);

  const [innerQuery, setInnerQuery] = useState(query || '');

  const filteredItems = filterItems
    ? items.filter((item) => filterItems(item, innerQuery))
    : items;

  const slicedItems = filteredItems.slice(0, 50);

  useEffect(() => {
    setIsOpen(defaultIsOpen);
  }, [defaultIsOpen]);

  const selectItem = (index: number) => {
    setSelectedIndex(index);
    onSelect && onSelect(slicedItems[index]);
    if (closesOnSelect) setIsOpen(false);
  };

  useEffect(() => {
    let isSub = true;
    if (value === undefined || value === null) {
      setSelectedIndex(0);
      setSelectedItem(undefined);
    } else {
      setSelectedIndex(slicedItems.findIndex((i) => i.id === value));
      if (resolveSelectedValue) {
        resolveSelectedValue(value).then((v) => isSub && setSelectedItem(v));
      } else {
        if (!items.find((i) => i.id === value)) {
          console.warn(
            'Selected index does not resolve to an item. Please ensure that the selected item is in the items list.'
          );
        }
        setSelectedItem(items.find((i) => i.id === value));
      }
    }
    return () => {
      isSub = false;
    };
    // We must listen for innerQuery changes - otherwise the selected index might become inconsistent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, innerQuery]);

  useEffect(() => {
    if (activeRef && scrollContainer.current) {
      const rect = activeRef.getBoundingClientRect();
      const containerRect = scrollContainer.current.getBoundingClientRect();

      // If we are near bottom, we need to compute where to scroll the container to
      if (rect.bottom >= containerRect.bottom - 30) {
        scrollContainer.current.scrollTo({
          top: activeRef.offsetTop - containerRect.height,
        });
        // But if we are near top, then simply scroll into view.
      } else if (rect.top <= containerRect.top) {
        activeRef.scrollIntoView();
      }
    }
  }, [selectedIndex, activeRef, scrollContainer]);

  return (
    <Popover
      className={classNames(className, fill && 'w-full')}
      placement={placement}
      disabled={disabled}
      tabIndex={props.tabIndex}
      isOpen={isOpen}
      onOpen={() => {
        setIsOpen(true);
        onOpen && onOpen();
      }}
      onClose={() => {
        setIsOpen(false);
        onClose && onClose();
      }}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key === 'ArrowUp') {
          setSelectedIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === 'ArrowDown') {
          setSelectedIndex((i) => Math.min(slicedItems.length - 1, i + 1));
        }
        if (e.key === 'Enter') {
          selectItem(selectedIndex);
        }
      }}
      offset={0}
      content={(close) => (
        <Menu
          minWidth={detailViewRenderer ? 600 : 300}
          display="flex"
          maxHeight={'400px'}
          paddingBottom={0}
        >
          <Box width="100%">
            {title && (
              <>
                <Heading size={300} padding={majorScale(1)} marginTop={2}>
                  {title}
                </Heading>
                <MenuDivider />
              </>
            )}
            <Box maxWidth="400px">
              {!hideSearchInput && (
                <InputGroup
                  placeholder={searchPlaceholder}
                  margin={minorScale(1)}
                  marginTop={title ? 0 : minorScale(1)}
                  width="calc(100% - 8px)"
                  autoFocus
                  value={innerQuery}
                  onChange={(e) => {
                    setInnerQuery(e.target.value);
                    onQueryChange && onQueryChange(e.target.value);
                  }}
                  isLoading={!!innerQuery && isLoading}
                />
              )}
              <Box
                maxHeight={300}
                overflowY="auto"
                paddingBottom={minorScale(1)}
                ref={scrollContainer}
                marginTop={hideSearchInput && !title ? minorScale(1) : 0}
              >
                {slicedItems.map((item, i) => (
                  <Box
                    ref={(el: HTMLDivElement) =>
                      i === selectedIndex && setActiveRef(el)
                    }
                    key={item.id}
                    display="flex"
                    alignItems="baseline"
                    onClick={(e: any) => {
                      e.stopPropagation();
                      e.preventDefault();
                      selectItem(i);
                    }}
                    className={classNames(
                      'item bordered',
                      i === selectedIndex && 'active'
                    )}
                  >
                    {itemRenderer(item)}
                  </Box>
                ))}
                {items.length === 0 && !isLoading && (
                  <Box
                    paddingY={majorScale(2)}
                    paddingX={majorScale(1)}
                    textAlign="center"
                  >
                    <Icon icon="search" size={majorScale(3)} />
                    <p className="p muted small">{noResultsMessage}</p>
                  </Box>
                )}
                {isLoading && (
                  <Box
                    paddingY={majorScale(2)}
                    paddingX={majorScale(1)}
                    display="flex"
                    justifyContent="center"
                    width="100%"
                  >
                    <Spinner />
                  </Box>
                )}
                {slicedItems.length < filteredItems.length && (
                  <Box
                    paddingY={majorScale(2)}
                    paddingX={majorScale(1)}
                    textAlign="center"
                  >
                    <p className="p muted small">
                      <FormattedMessage
                        values={{
                          count: filteredItems.length - slicedItems.length,
                        }}
                        defaultMessage="{count, plural, =0 {No results} one {# result} other {# results}}"
                      />
                    </p>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          {detailViewRenderer && (
            <Box
              width="100%"
              maxWidth="400px"
              minWidth="300px"
              borderLeft={`2px solid ${defaultColors.gray[100]}`}
              maxHeight={'400px'}
              overflowY="auto"
              marginTop={2}
            >
              {detailViewRenderer(
                selectedIndex < items.length ? items[selectedIndex] : undefined
              )}
            </Box>
          )}
        </Menu>
      )}
    >
      {children ? (
        children
      ) : (
        <Button
          marginBottom={minorScale(1)}
          {...props}
          disabled={disabled}
          width={fill ? '100%' : undefined}
          minHeight={32}
          iconRight={
            <Box display="flex" flexDirection="column">
              <Icon icon="chevron-up" size={12} marginBottom={-2} />
              <Icon icon="chevron-down" size={12} marginTop={-2} />
            </Box>
          }
        >
          {placeholder && (value === undefined || value === null) && (
            <Box is="span" color={defaultColors.gray[400]}>
              {placeholder}
            </Box>
          )}
          {selectedItem !== undefined && valueRenderer(selectedItem)}
        </Button>
      )}
    </Popover>
  );
};

export interface IHTMLSelectProps extends BoxProps<any> {
  placeholder?: string;
  options?: { label: ReactNode; value: string | number }[];
  value?: string | number | undefined;
  defaultValue?: string | number | undefined;
  name?: string;
  fill?: boolean;
  onChange?: (value: string | number | undefined) => void;
}

export const HTMLSelect: React.FC<IHTMLSelectProps> = forwardRef<
  HTMLSelectElement,
  IHTMLSelectProps
>(
  (
    {
      placeholder,
      children,
      options = [],
      name,
      value,
      defaultValue,
      onChange,
      onBlur,
      ...props
    },
    ref
  ) => {
    const [innerValue, setInnerValue] = useState(value);

    useEffect(() => {
      setInnerValue(value);
    }, [value]);

    return (
      <Box is="label" position="relative" {...props} flexShrink={0}>
        <Box
          display="flex"
          flexDirection="column"
          top="calc(50% - 2px)"
          transform="translateY(-50%)"
          right={majorScale(1)}
          position="absolute"
          pointerEvents="none"
        >
          <Icon icon="chevron-up" size={12} marginBottom={-2} />
          <Icon icon="chevron-down" size={12} marginTop={-2} />
        </Box>
        <Box
          ref={ref}
          is="select"
          className="button"
          paddingRight="32px"
          height={32}
          marginBottom={minorScale(1)}
          name={name}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            setInnerValue(e.target.value);
            onChange && onChange(e.target.value);
          }}
          onBlur={onBlur}
          value={innerValue}
          defaultValue={defaultValue}
          color={
            placeholder && innerValue === undefined
              ? defaultColors.gray[400]
              : undefined
          }
          width="100%"
        >
          {placeholder && innerValue === undefined && (
            <option value="">{placeholder}</option>
          )}
          {options.map((option, i) => (
            <option key={option.value || `index-${i}`} value={option.value}>
              {option.label}
            </option>
          ))}
        </Box>
      </Box>
    );
  }
);
